import {
  Box,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { startTransition, useState } from 'react';
import { z } from 'zod';

import { type Terminart, terminart as terminartEnum } from '../../../../../dtos';
import { printTerminart } from '../../../../../utils/printTerminart';
import { content } from './VeranstaltungPropertiesChanger.content.ts';
import { printAusschreibungsprozessTooltip, printTerminartTooltip } from './VeranstaltungPropertiesChanger.utils';

type TerminartToggleButtonProps = { readonly terminart: NonNullable<Terminart> };
const TerminartToggleButton = ({ terminart }: TerminartToggleButtonProps) => (
  <Tooltip
    disableInteractive
    title={
      <Typography variant="paragraphSm" color="textPrimaryOnBrand">
        {printTerminartTooltip(terminart)}
      </Typography>
    }
  >
    <ToggleButton value={terminart}>{printTerminart(terminart)}</ToggleButton>
  </Tooltip>
);

type VeranstaltungPropertiesChangerProps = {
  readonly terminart: NonNullable<Terminart>;
  readonly onTerminartChange: (terminart: NonNullable<Terminart>) => void;
  readonly manuellerAusschreibungsprozess: boolean;
  readonly withRessourcen?: boolean;
  readonly onWithRessourcenChange?: (withResources: boolean) => void;
  readonly hideWithRessourcen?: boolean;
};

function isNonNullableTerminart(value: unknown): value is NonNullable<Terminart> {
  return z.enum(terminartEnum).safeParse(value).success;
}

export const VeranstaltungPropertiesChanger = ({
  terminart,
  withRessourcen,
  manuellerAusschreibungsprozess,
  onTerminartChange,
  onWithRessourcenChange,
  hideWithRessourcen,
}: VeranstaltungPropertiesChangerProps) => {
  const [localTerminart, setLocalTerminart] = useState<NonNullable<Terminart>>(terminart);
  const [localWithResources, setLocalWithResources] = useState(Boolean(withRessourcen));

  const handleTerminartChange = (_: React.MouseEvent, value: unknown): void => {
    if (!isNonNullableTerminart(value)) {
      return;
    }

    // Update local state immediately to instantly show the radio button switching
    setLocalTerminart(value);

    // Update parent state with low priority since re-rendering the calendar will take longer and shouldn't block the radio button switching
    startTransition(() => {
      onTerminartChange(value);
    });
  };

  const handleWithResourcesChange = (_: unknown, value: boolean): void => {
    // Update local state immediately to instantly show the radio button switching
    setLocalWithResources(value);

    // Update parent state with low priority since re-rendering the calendar will take longer and shouldn't block the radio button switching
    startTransition(() => {
      if (onWithRessourcenChange) {
        onWithRessourcenChange(value);
      }
    });
  };

  return (
    <Paper sx={{ border: 'none', p: 0 }}>
      <Stack direction="row" sx={{ py: 3 }}>
        <Stack direction="column" spacing={2} justifyContent="center" flexGrow={1}>
          <Box>
            <ToggleButtonGroup value={localTerminart} exclusive onChange={handleTerminartChange}>
              <TerminartToggleButton terminart={terminartEnum.STANDARD} />
              <TerminartToggleButton terminart={terminartEnum.ZUSATZ} />
              <TerminartToggleButton terminart={terminartEnum.ERSATZ} />
            </ToggleButtonGroup>
          </Box>
        </Stack>
        {!hideWithRessourcen && (
          <Stack direction="column" spacing={2} justifyContent="center" flexGrow={1}>
            <FormControlLabel
              control={
                <Switch
                  aria-label={content.ressourcenWaehlen}
                  onChange={handleWithResourcesChange}
                  checked={localWithResources}
                />
              }
              label={<Typography variant="labelMultilineLight">{content.ressourcenWaehlen}</Typography>}
              slotProps={{ typography: { sx: { cursor: 'default' } } }}
              sx={{ width: 'fit-content' }}
            />
          </Stack>
        )}
        <Stack direction="column" spacing={2} justifyContent="center" flexGrow={1}>
          <FormControlLabel
            control={
              <Tooltip
                disableInteractive
                title={
                  <Typography variant="paragraphSm" color="textPrimaryOnBrand">
                    {printAusschreibungsprozessTooltip()}
                  </Typography>
                }
              >
                <span>
                  <Switch disabled checked={manuellerAusschreibungsprozess} />
                </span>
              </Tooltip>
            }
            label={<Typography variant="labelMultilineLight">{content.manuellerAusschreibungsprozess}</Typography>}
            slotProps={{ typography: { sx: { cursor: 'default' } } }}
            sx={{ '& > span': { color: 'unset !important' }, width: 'fit-content' }}
            disabled
          />
        </Stack>
      </Stack>
    </Paper>
  );
};
